import { useState, useEffect, useCallback } from 'react';
import { fetchExchangeRates, CURRENCIES } from '../services/currencyService';

const STORAGE_KEY = 'selected_currency';
const RATES_CACHE_KEY = 'currency_rates';
const CACHE_DURATION = 6 * 60 * 60 * 1000;

export const useCurrency = () => { // ✅ named export
  const [selectedCurrency, setSelectedCurrency] = useState(() => {
    return localStorage.getItem(STORAGE_KEY) || 'USD';
  });

  const [rates, setRates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const loadRates = useCallback(async (force = false) => {
    if (!force) {
      const cached = localStorage.getItem(RATES_CACHE_KEY);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < CACHE_DURATION) {
          setRates(data.rates);
          setLastUpdated(data.date);
          setLoading(false);
          return;
        }
      }
    }

    setLoading(true);
    try {
      const data = await fetchExchangeRates();
      setRates(data.rates);
      setLastUpdated(data.date);

      localStorage.setItem(RATES_CACHE_KEY, JSON.stringify({
        data: { rates: data.rates, date: data.date },
        timestamp: Date.now()
      }));

      setError(null);
    } catch (err) {
      setError(err.message);
      const cached = localStorage.getItem(RATES_CACHE_KEY);
      if (cached) {
        const { data } = JSON.parse(cached);
        setRates(data.rates);
        setLastUpdated(data.date);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRates();
  }, [loadRates]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, selectedCurrency);
  }, [selectedCurrency]);

  const convertPrice = useCallback((priceUSD) => {
    if (!rates || !rates[selectedCurrency]) return priceUSD;
    return priceUSD * rates[selectedCurrency];
  }, [rates, selectedCurrency]);

  const formatPrice = useCallback((priceUSD) => {
    const converted = convertPrice(priceUSD);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: selectedCurrency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      useGrouping: false
    }).format(converted);
  }, [convertPrice, selectedCurrency]);

  const refreshRates = useCallback(() => {
    loadRates(true);
  }, [loadRates]);

  return {
    selectedCurrency,
    setSelectedCurrency,
    rates,
    loading,
    error,
    lastUpdated,
    convertPrice,
    formatPrice,
    refreshRates,
    availableCurrencies: CURRENCIES
  };
};